import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TallyReportView } from '@/components/company/reports/tally/tally-report-view';
import { useCompany } from '@/hooks/use-company';
import { findTallyReport } from '@/lib/tally-reports';

export const Route = createFileRoute('/(app)/company/reports/tally/$report')({
  component: TallyReportPage,
});

/**
 * One page for every Tally report, chosen by the URL.
 *
 * A route parameter rather than a file each: the reports differ only in which
 * inputs they take, and six near-identical files would drift into six slightly
 * different tables. An unrecognised name is answered here rather than passed
 * on — Tally Engine would refuse it correctly, but a mistyped URL should not
 * look like the books failing to load.
 */
function TallyReportPage() {
  const { report: reportId } = Route.useParams();
  const { selectedCompanyId } = useCompany();
  const definition = findTallyReport(reportId);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/company/reports">
            <ArrowLeft className="mr-2 size-4" />
            Reports
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{definition?.title ?? 'Unknown report'}</CardTitle>
          <CardDescription>
            {definition?.description ?? 'There is no report by that name.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {definition && selectedCompanyId ? (
            <TallyReportView companyId={selectedCompanyId} definition={definition} />
          ) : !definition ? (
            <p className="text-sm text-muted-foreground">
              Pick one from the <Link to="/company/reports" className="underline">reports page</Link>.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Select a company first.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
